<template>
  <div :class="orientation === 'row' ? 'flex flex-wrap gap-x-8 gap-y-1' : 'flex flex-col gap-2'">
    <div v-for="locale in ALLOWED_LOCALES" :key="locale" class="flex items-center">
      <TooltipWrapper top>
        <template #trigger>
          <button
            type="button"
            class="btn-link-color-s inline-flex items-center whitespace-nowrap"
            :data-testid="`generic-invitation-link-copy-${locale}`"
            @click="copyUrl(locale)"
          >
            <component :is="locale === 'de' ? GermanCustomIcon : EnglishCustomIcon" aria-hidden="true" />
            <span class="sr-only">{{ locale.toUpperCase() }}</span>
            {{ t(`Reviews.${subject}.campaign.copySurveyLink.text`) }}
            <CopyIcon class="mx-1 lucide-icon-s" aria-hidden="true" />
          </button>
        </template>
        <template #content>
          <div>{{ t(`Reviews.${subject}.campaign.copySurveyLink.tooltip`) }}</div>
        </template>
      </TooltipWrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { CopyIcon, EnglishCustomIcon, GermanCustomIcon } from '@ramp106/omrjs-core-ui/icons'
import { reviewsPaths } from '@ramp106/omrjs-routing'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useI18n } from 'vue-i18n'

import { baseOmrUrl } from '@/config/env'
import { showNotification } from '@/helpers/notificationHelper'
import { ALLOWED_LOCALES } from '@/helpers/translationsHelper'

const props = withDefaults(
  defineProps<{
    subject: 'product' | 'agency'
    slug: string
    ctaPosition: 'agency_reviews' | 'product_campaigns' | 'product_campaign_links'
    orientation?: 'row' | 'column'
    campaignHash?: string
  }>(),
  { orientation: 'column', campaignHash: undefined },
)

const { t } = useI18n()

function surveyUrl(locale: string) {
  const path =
    props.subject === 'agency'
      ? reviewsPaths.agencyReview({ locale, slug: props.slug })
      : reviewsPaths.productTrustBuilding({ locale, slug: props.slug }, props.campaignHash ? { campaign: props.campaignHash } : undefined)
  return `${baseOmrUrl}${path}`
}

function copyUrl(locale: string) {
  track('cta_click', {
    surface: 'cta',
    category: 'engagement',
    cta_id: 'survey_link_copy',
    cta_position: props.ctaPosition,
  })
  navigator.clipboard
    .writeText(surveyUrl(locale))
    .then(() => {
      showNotification(t(`Reviews.${props.subject}.campaign.copySuccess`), 'success')
    })
    .catch((err) => {
      console.error('Failed to copy URL:', err)
    })
}
</script>
